import { Button, ConfirmDialog, DialogHandler } from "@etain/ui";
import type { FormEvent, ReactNode, RefObject } from "react";
import { useContext, useEffect, useId, useRef, useState } from "react";
import { UNSAFE_DataRouterContext, useBlocker } from "react-router-dom";

/** Includes unnamed controls and unchecked checkboxes, unlike FormData. */
function formSnapshot(form: HTMLFormElement): string {
  return JSON.stringify(
    Array.from(form.elements).flatMap<unknown>((element) => {
      if (element instanceof HTMLInputElement) {
        if (["submit", "button", "reset"].includes(element.type)) return [];
        return [[element.name, element.value, element.checked]];
      }
      if (element instanceof HTMLSelectElement) {
        return [[element.name, Array.from(element.selectedOptions, (option) => option.value)]];
      }
      if (element instanceof HTMLTextAreaElement) return [[element.name, element.value]];
      return [];
    }),
  );
}

function DiscardConfirmation({
  open,
  onCancel,
  onDiscard,
}: {
  open: boolean;
  onCancel: () => void;
  onDiscard: () => void;
}) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(value) => !value && onCancel()}
      title="Änderungen verwerfen?"
      description="Ihre ungespeicherten Eingaben gehen verloren."
      cancelAction={
        <Button variant="secondary" onClick={onCancel}>
          Weiter bearbeiten
        </Button>
      }
      confirmAction={
        <Button variant="danger" onClick={onDiscard}>
          Änderungen verwerfen
        </Button>
      }
      destructive
      children={null}
    />
  );
}

function NavigationGuard({ when, canLeave }: { when: boolean; canLeave: () => boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when &&
      !canLeave() &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  );
  return (
    <DiscardConfirmation
      open={blocker.state === "blocked"}
      onCancel={() => blocker.state === "blocked" && blocker.reset()}
      onDiscard={() => blocker.state === "blocked" && blocker.proceed()}
    />
  );
}

const focusableSelector =
  "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

function resolveFocusableElement(value: EventTarget | null): HTMLElement | null {
  if (!(value instanceof Element)) return null;
  return value.closest<HTMLElement>(focusableSelector);
}

/**
 * Leitet einen lesbaren, aber nicht zwingend eindeutigen Anteil der Formular-ID
 * aus dem Titel ab. Umlaute werden per NFD-Normalisierung auf ihren Basisbuchstaben
 * reduziert (ä → a, ö → o, ü → u), bevor alles Nicht-ASCII entfernt wird.
 */
function slugifyTitle(title: string): string {
  return title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Fokussiert das erste erreichbare Ziel der Fallback-Kette (Element muss verbunden und aktiv sein). */
function focusFallbackTarget() {
  const mainContent = document.getElementById("main-content");
  if (mainContent instanceof HTMLElement) {
    mainContent.focus({ preventScroll: true });
    return;
  }
  document.body.focus({ preventScroll: true });
}

export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  busy,
  onSubmit,
  onChange,
  children,
  size,
  submitVariant,
  submitDisabled,
  presentation = "dialog",
  error,
  dirty,
  destructiveAction,
  guardChanges = true,
  afterForm,
  secondaryActions,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onChange?: () => void;
  children: ReactNode;
  size?: "small" | "medium" | "large";
  submitVariant?: "primary" | "danger";
  submitDisabled?: boolean;
  presentation?: "dialog" | "page";
  error?: ReactNode;
  /** For rich-text editors and changes outside native form controls. */
  dirty?: boolean;
  destructiveAction?: ReactNode;
  /** Opt out only when the caller already owns a complete discard guard. */
  guardChanges?: boolean;
  /** Independently saved subforms must not be nested in the main HTML form. */
  afterForm?: ReactNode;
  secondaryActions?: ReactNode | ((formId: string) => ReactNode);
  /** Stable trigger for dialogs opened from a menu whose item is removed on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  // useId() macht die ID kollisionsfrei (mehrere gleich betitelte Dialoge auf
  // einer Seite erzeugten sonst doppelte form-IDs); der Titel-Slug hält sie lesbar.
  const uniqueSuffix = useId().replace(/[^a-zA-Z0-9]/g, "");
  const formId = `form-${slugifyTitle(title)}-${uniqueSuffix}`;
  // Wird der Dialog bereits geöffnet eingehängt (z. B. per wechselndem key), fokussiert ein
  // autoFocus-Feld den Inhalt noch vor Radix' onOpenAutoFocus. Der Auslöser ist deshalb nur
  // beim ersten Rendern sicher als aktives Element bekannt.
  const [mountedFocusTarget] = useState(() =>
    open && typeof document !== "undefined"
      ? resolveFocusableElement(document.activeElement)
      : null,
  );
  const returnFocusRef = useRef<HTMLElement | null>(mountedFocusTarget);
  const navigationAllowed = useRef(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const initialSnapshot = useRef<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const [changed, setChanged] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [previousOpen, setPreviousOpen] = useState(open);
  if (previousOpen !== open) {
    setPreviousOpen(open);
    if (!open) {
      setChanged(false);
      setDiscardOpen(false);
    }
  }
  const router = useContext(UNSAFE_DataRouterContext);
  const hasChanges = dirty === true || changed;

  useEffect(() => {
    if (open) navigationAllowed.current = false;
    if (open && formRef.current && !hasChanges) {
      initialSnapshot.current = formSnapshot(formRef.current);
    }
    if (!open) {
      initialSnapshot.current = null;
    }
  }, [open, children, hasChanges]);

  useEffect(() => {
    if (!open || !hasChanges || !guardChanges) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [open, hasChanges, guardChanges]);

  useEffect(() => {
    if (open && error) errorRef.current?.focus({ preventScroll: false });
  }, [open, error]);

  function requestOpenChange(value: boolean) {
    if (!value && busy) return;
    if (!value && hasChanges && guardChanges) {
      setDiscardOpen(true);
      return;
    }
    if (!value) navigationAllowed.current = true;
    onOpenChange(value);
  }

  useEffect(() => {
    if (open) return;

    function rememberFocusTarget(event: Event) {
      const focusTarget = resolveFocusableElement(event.target);
      if (focusTarget) returnFocusRef.current = focusTarget;
    }

    document.addEventListener("pointerdown", rememberFocusTarget, true);
    document.addEventListener("focusin", rememberFocusTarget, true);
    return () => {
      document.removeEventListener("pointerdown", rememberFocusTarget, true);
      document.removeEventListener("focusin", rememberFocusTarget, true);
    };
  }, [open, presentation]);

  useEffect(() => {
    if (!open || presentation !== "page") return;
    return () => {
      queueMicrotask(() => {
        const target = returnFocusRef.current;
        if (target?.isConnected && !target.matches(":disabled"))
          target.focus({ preventScroll: true });
        else focusFallbackTarget();
      });
    };
  }, [open, presentation]);

  function handleOpenAutoFocus() {
    const activeElement = resolveFocusableElement(document.activeElement);
    if (activeElement instanceof HTMLButtonElement || activeElement instanceof HTMLAnchorElement) {
      returnFocusRef.current = activeElement;
    } else if (!returnFocusRef.current) {
      returnFocusRef.current = activeElement;
    }
  }

  function handleCloseAutoFocus(event: Event) {
    event.preventDefault();
    const target = restoreFocusRef?.current ?? returnFocusRef.current;
    returnFocusRef.current = null;
    if (target?.isConnected && !target.matches(":disabled")) {
      target.focus({ preventScroll: true });
      return;
    }
    // Kein auslösendes Element bekannt (z. B. Dialog wurde über einen
    // URL-Parameter geöffnet) – Fokus geht sonst verloren und landet auf <body>.
    focusFallbackTarget();
  }

  const cancelAction = (
    <Button
      type="button"
      variant="secondary"
      onClick={() => requestOpenChange(false)}
      disabled={busy}
    >
      Abbrechen
    </Button>
  );
  const primaryAction = (
    <Button
      type="submit"
      form={formId}
      busy={busy}
      variant={submitVariant}
      disabled={busy || submitDisabled}
    >
      {submitLabel}
    </Button>
  );
  const form = (
    <form
      id={formId}
      ref={(node) => {
        formRef.current = node;
        if (node && initialSnapshot.current === null) initialSnapshot.current = formSnapshot(node);
      }}
      className="dialog-form"
      onSubmit={onSubmit}
      onChange={() => {
        if (formRef.current) setChanged(formSnapshot(formRef.current) !== initialSnapshot.current);
        onChange?.();
      }}
    >
      {error ? (
        <div ref={errorRef} className="ui-dialog__error" role="alert" tabIndex={-1}>
          {error}
        </div>
      ) : null}
      {children}
    </form>
  );

  return (
    <>
      <DialogHandler
        open={open}
        onOpenChange={requestOpenChange}
        title={title}
        description={description}
        size={size}
        presentation={presentation}
        destructiveAction={destructiveAction}
        cancelAction={cancelAction}
        primaryAction={primaryAction}
        secondaryActions={
          typeof secondaryActions === "function" ? secondaryActions(formId) : secondaryActions
        }
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        {form}
        {afterForm}
      </DialogHandler>
      <DiscardConfirmation
        open={discardOpen}
        onCancel={() => setDiscardOpen(false)}
        onDiscard={() => {
          navigationAllowed.current = true;
          setDiscardOpen(false);
          setChanged(false);
          onOpenChange(false);
        }}
      />
      {router && open && guardChanges ? (
        <NavigationGuard when={hasChanges} canLeave={() => navigationAllowed.current} />
      ) : null}
    </>
  );
}
